import type { ReactNode } from 'react';
import { useT, type Locale } from '../i18n/index.js';

/**
 * Screen titles painted into their artwork, one picture per language.
 *
 * Live gold laid over a painted sign read as a sticker: sharper than the
 * picture, lit by nothing, on top of the wood instead of in it. These are
 * painted onto the empty plank of the screen's own art, and only the patch
 * that carries the word is cut out, its edges fading to nothing, then laid
 * exactly back over the same spot — so the paint sits in the grain like the
 * rest of the picture.
 *
 * A language without its picture keeps the live title it is given, so no
 * screen goes blank while the set is being painted.
 *
 * Each picture is either ChatGPT's own painting of the word on the empty sign
 * or, for a language it was not asked for, the word painted onto the same
 * surface with the same paint, sampled from ChatGPT's and in its proportions:
 * the paint stays out of the gaps and cracks and thins over the deep grain,
 * as ChatGPT's does. A screen's pictures are all the same patch of its art, so
 * one rule places them (380 x 134 on the Tasks hero, 284 x 98 on the Wallet
 * scene, 500 x 145 on the Profile screen's 2x master, 445 x 96 on the Invite
 * screen). Portuguese and Spanish share PERFIL. Invite's title is carved into
 * the stone and gilded rather than painted: Spanish and English by ChatGPT,
 * the other four carved here to match them, in the same gold and size.
 */
const PAINTED: Partial<Record<string, Partial<Record<Locale, string>>>> = {
  tasks: {
    es: '/ui/tasks/title-es.webp',
    en: '/ui/tasks/title-en.webp',
    pt: '/ui/tasks/title-pt.webp',
    ru: '/ui/tasks/title-ru.webp',
    id: '/ui/tasks/title-id.webp',
    ar: '/ui/tasks/title-ar.webp',
  },
  wallet: {
    es: '/ui/wallet/title-es.webp',
    en: '/ui/wallet/title-en.webp',
    pt: '/ui/wallet/title-pt.webp',
    ru: '/ui/wallet/title-ru.webp',
    id: '/ui/wallet/title-id.webp',
    ar: '/ui/wallet/title-ar.webp',
  },
  profile: {
    es: '/ui/profile/title-es.webp',
    pt: '/ui/profile/title-es.webp',
    en: '/ui/profile/title-en.webp',
    ru: '/ui/profile/title-ru.webp',
    id: '/ui/profile/title-id.webp',
    ar: '/ui/profile/title-ar.webp',
  },
  invite: {
    es: '/ui/invite/title-es.webp',
    en: '/ui/invite/title-en.webp',
    pt: '/ui/invite/title-pt.webp',
    ru: '/ui/invite/title-ru.webp',
    id: '/ui/invite/title-id.webp',
    ar: '/ui/invite/title-ar.webp',
  },
  /* The level-up's burst, cut with its rays from the animation's own sheet. */
  levelup: {
    es: '/ui/levelup/title-es.webp',
  },
  /* Activity, in the Tasks sign's paint (tools/paint_activity_titles.py). */
  activity: {
    es: '/ui/activity/title-es.webp',
    en: '/ui/activity/title-en.webp',
    pt: '/ui/activity/title-pt.webp',
    ru: '/ui/activity/title-ru.webp',
    id: '/ui/activity/title-id.webp',
    ar: '/ui/activity/title-ar.webp',
  },
};

/** Whether this screen's title is painted in this language (the rest keep the live one). */
export function hasPaintedTitle(name: string, locale: Locale): boolean {
  return Boolean(PAINTED[name]?.[locale]);
}

export function PaintedTitle({
  name,
  label,
  className,
  as: Tag = 'h1',
  children,
}: {
  /** Which screen's title (a key of PAINTED). */
  name: string;
  /** The title in words: the heading's name for screen readers. */
  label: string;
  /** Places the picture over the patch of art it was cut from. */
  className: string;
  /** The element the live title is, so the painted one keeps its role. */
  as?: 'h1' | 'span';
  /** The live title, kept when this language has no picture. */
  children: ReactNode;
}) {
  const { locale } = useT();
  const src = PAINTED[name]?.[locale];
  if (!src) return <>{children}</>;
  return (
    <Tag className={className}>
      <img src={src} alt={label} draggable={false} />
    </Tag>
  );
}
